import { styled } from 'styled-components';
import React, {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { Styling } from '@/styling';
import { flexGap } from '@/flexGap';
import { Divider } from '@/components/visual/Divider';
import { Typography } from '@/components/text/Typography';

const Container = styled.div`
  position: relative;
`;

const ProfileButton = styled.button.attrs({
  'aria-label': 'Open profile menu', // TODO translate
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: ${Styling.spacing(0, 1.5)};
  padding-top: 12px;
  ${({ theme }) => flexGap(theme.spacing[1])};
  background-color: ${({ theme }) => theme.palette.grayscale.light5};
  color: ${({ theme }) => theme.palette.grayscale.main};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  overflow: hidden;

  & > * {
    flex-shrink: 0;
  }
`;

const Menu = styled.ul`
  position: absolute;
  min-width: 100%;
  margin-top: -1px;
  top: 100%;
  right: 0;
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  box-shadow: 0 8px 16px 0 #7e7f805c;
  border-radius: ${Styling.radii('button', 0, 'button', 'button')};
  overflow: hidden;
`;

const MenuItem = styled.li`
  color: ${({ theme }) => theme.palette.primary.text};
  padding: ${Styling.spacing(1.5, 2)};
  text-align: right;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.grayscale.light5};
  }
`;

export type MobileProfileMenuProps = {
  items: (
    | {
        content: ReactNode;
        onClick?(): void;
      }
    | 'divider'
  )[];
};

export const MobileProfileMenu: FC<MobileProfileMenuProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    },
    [isOpen, containerRef.current],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);

    return () => document.removeEventListener('mousedown', handleClick);
  }, [handleClick]);

  return (
    <Container ref={containerRef}>
      <ProfileButton onClick={() => setIsOpen((open) => !open)}>
        <FontAwesomeIcon icon={faUser} size="lg" />
      </ProfileButton>
      {isOpen && (
        <Menu>
          <Typography type="body">
            {items.map((item, index) => {
              if (item === 'divider') {
                return (
                  <Fragment key={index}>
                    <Divider orientation="horizontal" />
                  </Fragment>
                );
              }

              return (
                <MenuItem
                  key={index}
                  onClick={() => {
                    setIsOpen(false);
                    item.onClick?.();
                  }}
                >
                  {item.content}
                </MenuItem>
              );
            })}
          </Typography>
        </Menu>
      )}
    </Container>
  );
};
